import { useAtomValue } from "jotai";
import type { GetStaticProps } from "next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import useLocation from "@brexhq/next/src/hooks/useLocation";
import partners from "@brexhq/next/src/utils/redirects/partners.json";

import { buildLayoutProps } from "../layouts/layoutUtils";
import { PageLoading } from "../src/components/shared/PageLoading/PageLoading";
import useAnalytics from "../src/hooks/useAnalytics";
import NextPageBuilderRenderer from "../src/modules/PageBuilder/NextPageBuilderRenderer";
import PreviewBanner from "../src/modules/PreviewBanner/PreviewBanner";
import { referrerAtom } from "../src/state/prevPath";
import { PageNotFoundEvent } from "../src/telemetry/analyticsEvents";
import {
  get404PageSettings,
  getHomePageById,
  resolveAllReferences,
} from "../src/utils/dataFetching/api";
import { parseContext } from "../src/utils/dataFetching/getStaticPropsUtils";
import {
  buildRepeatableProps,
  getFilteredArticlesForNextPage,
} from "../src/utils/pageBuild/repeatableContent";
import { BrexNextPage } from "../types/BrexNextPage";

const Page404: BrexNextPage<{
  pageBuilder: any;
  preview: boolean;
  updatedAt: string;
}> = ({
  pageBuilder,
  preview,
  updatedAt,
  repeatableArticles,
  repeatableBundles,
}) => {
  const [shouldShow404, setShouldShow404] = useState(false);
  const location = useLocation();
  const { asPath } = useRouter();
  const { trackEvent } = useAnalytics();
  const prevPath = useAtomValue(referrerAtom);
  const REDIRECT_PARAM = "rdr";

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasRedirectParam = searchParams.has(REDIRECT_PARAM);

    const referrer = prevPath || document?.referrer || "";
    const currentUrl = new URL(window.location.href);

    // WE need to account for a special case of the directory page as it proxies through to a 3rd party service
    if (asPath.includes("directory") && !hasRedirectParam) {
      currentUrl.searchParams.set(REDIRECT_PARAM, "1");
      window.location.href = currentUrl.href;
    } else {
      // if a user comes to /yc instead of /partners/yc we need to redirect them on the client side
      let rdrUrl;
      if (partners[asPath]) {
        // Check for a match with asPath, which includes URL params
        rdrUrl = new URL(partners[asPath], location.origin);
      } else if (partners[currentUrl.pathname]) {
        // Check for a match with just the pathname and maintain query params if they are present
        const rdrPath = searchParams.toString().length
          ? `${partners[currentUrl.pathname]}?${searchParams}`
          : partners[currentUrl.pathname];
        rdrUrl = new URL(rdrPath, location.origin);
      }

      if (rdrUrl) {
        window.location.href = rdrUrl.href;
        return;
      }

      setShouldShow404(true);

      if (!preview) {
        return trackEvent(new PageNotFoundEvent(location.href ?? "", referrer));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!shouldShow404) return <PageLoading />;
  return (
    <>
      <PreviewBanner
        isPreviewMode={preview}
        slug={"404"}
        updatedAt={updatedAt}
      />
      <NextPageBuilderRenderer
        blocks={pageBuilder}
        repeatContentData={{
          repeatableArticles,
          repeatableBundles,
        }}
      />
      ;
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { preview, previewData } = parseContext(context);
  const {
    data: { HomeAnd404Settings },
  } = await get404PageSettings({ preview, previewData });

  // get the draft 404 page
  const { _id } = HomeAnd404Settings.page404;
  const pageId = preview ? `drafts.${_id}` : _id;
  const preview404Page =
    preview && _id
      ? await getHomePageById({
          id: pageId,
          preview,
          previewData,
        })
      : undefined;
  const { homePageContent, corePageSettings, meta, _updatedAt } =
    preview404Page?.data ?? HomeAnd404Settings.page404;

  const pageBuilder = await resolveAllReferences(
    homePageContent?.corePageBuilder,
    ["copyLibraryItem", "dynamicNumber"],
    previewData,
  );

  const pageSettings = corePageSettings;

  // Resolve references for additionalLegalFooterSection if it exists
  if (pageSettings && "additional" in pageSettings && pageSettings.additional) {
    pageSettings.additional = await resolveAllReferences(
      pageSettings.additional,
      ["copyLibraryItem", "dynamicNumber"],
      previewData,
    );
  }
  const filteredRepeatableContent = await getFilteredArticlesForNextPage({
    corePageBuilder: pageBuilder,
    pageId: pageId,
    previewData,
  });

  return {
    props: {
      updatedAt: _updatedAt,
      preview,
      pageBuilder,
      ...buildRepeatableProps(filteredRepeatableContent),
      layoutProps: await buildLayoutProps({
        meta,
        pageSettings,
        pageType: "homePage",
      }),
    },
  };
};

export default Page404;
